import PcProductCameraAssistant from "../../Broadcast/PcProductCameraAssistant";
import { createPortal } from "react-dom";
import LiveHostStatus from "../../../Component/UI/LiveHostStatus";
import useReviewHostStatus from "../../Broadcast/useReviewHostStatus";
import { drawBroadcastDeviceNotice } from "./broadcastDeviceNotice";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import { live_info_type } from "../../User/Live/Live";

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: #3a3939;
  overflow: hidden;
  box-sizing: border-box;
  position: absolute;
  inset: 0;
`;
const LiveContent = styled.div`
  width: 100%;
  height: 100%;
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  canvas {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

interface Live_Props {
  livestart: string;
  hostStatusTarget?: HTMLElement | null;
  productCameraTarget?: HTMLElement | null;
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
  selectedVideo_props: string;
  selectedAudio_props: string;
  live_info: live_info_type | undefined;
  onStarted: () => Promise<void>;
  onError: (error: unknown) => void;
  onCameraDisconnected?: () => void;
}

export interface AdminLiveHandle {
  stop: () => Promise<void>;
}

const AdminLive = forwardRef<AdminLiveHandle, Live_Props>(({livestart, selectedAudio_props, selectedVideo_props, live_info, onStarted, onError, onCameraDisconnected, hostStatusTarget, productCameraTarget, cameraEnabled = true, microphoneEnabled = true}, ref) => {
  const reviewHost = useReviewHostStatus();
  const [cameraDisconnected, setCameraDisconnected] = useState(false);
  const activeCamera = cameraEnabled && !cameraDisconnected;
  const [qrPublishing, setQrPublishing] = useState(false);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const mediaRef = useRef<MediaStream | undefined>(undefined);
  const noticeCanvasRef = useRef<HTMLCanvasElement | undefined>(undefined);
  const publishAudioStateRef = useRef<() => void>(() => {});
  const deviceEnabled = useRef({cameraEnabled, microphoneEnabled});
  deviceEnabled.current = {cameraEnabled: activeCamera, microphoneEnabled};
  useEffect(() => {
    const draw = () => {
      if (noticeCanvasRef.current) drawBroadcastDeviceNotice(noticeCanvasRef.current, activeCamera);
    };
    draw();
    mediaRef.current?.getTracks().forEach(track => {
      track.enabled = track.kind === 'video' ? activeCamera : microphoneEnabled;
    });
    publishAudioStateRef.current();
  }, [activeCamera, microphoneEnabled]);
  const [snapshotError, setSnapshotError] = useState("");
  const callbacks = useRef({onStarted, onError, onCameraDisconnected});
  callbacks.current = {onStarted, onError, onCameraDisconnected};
  const liveSeq = live_info?.seq;
  const isRehearsal = live_info?.live_status === '리허설';
  const stopBroadcast = useRef<() => Promise<void>>(async () => {});
  useImperativeHandle(ref, () => ({ stop: () => stopBroadcast.current() }), []);

  useEffect(() => {
    setQrPublishing(false);
    setSnapshotError("");
    if (livestart !== "start") return;
    setCameraDisconnected(false);
    let cancelled = false;
    let released = false;
    let media: MediaStream | undefined;
    let client: any;
    let broadcastToken: string | undefined;
    let startPending: Promise<unknown> | undefined;
    let cameraVideo: HTMLVideoElement | undefined;
    let cameraTrack: MediaStreamTrack | undefined;
    let cameraLost = false;
    const cameraHasEnded = () => cameraLost || cameraTrack?.readyState === 'ended';
    const onCameraEnded = () => {
      if (cancelled || released || cameraLost) return;
      cameraLost = true;
      deviceEnabled.current.cameraEnabled = false;
      if (noticeCanvasRef.current) drawBroadcastDeviceNotice(noticeCanvasRef.current, false);
      setCameraDisconnected(true);
      setQrPublishing(false);
      callbacks.current.onCameraDisconnected?.();
      publishAudioStateRef.current();
    };
    let fillCamera: (() => void) | undefined;
    let snapshotTimer: number | undefined;
    let audioStateTimer: number | undefined;
    const controller = new AbortController();
    const canvas = previewRef.current;
    const {onStarted: started, onError: failed} = callbacks.current;
    const release = async (reportFailure = false) => {
      if (released) return;
      released = true;
      if (cameraVideo && fillCamera) cameraVideo.removeEventListener('resize', fillCamera);
      cameraTrack?.removeEventListener?.('ended', onCameraEnded);
      window.clearTimeout(audioStateTimer);
      publishAudioStateRef.current = () => {};
      media?.getTracks().forEach(track => track.stop());
      if (mediaRef.current === media) { mediaRef.current = undefined; noticeCanvasRef.current = undefined; }
      if (startPending) { try { await startPending; } catch { /* Failed starts still need cleanup. */ } }
      if (client) {
        try { client.detachPreview(canvas); } catch { /* Preview may not be attached yet. */ }
        // Detaching IVS leaves its last frame painted in the canvas.
        if (canvas) { const width = canvas.width; canvas.width = width; }
        try { await client.stopBroadcast(); } catch (error) {
          if (reportFailure) { released = false; throw error; }
          // Keep the reservation until expiry when native stop could not be confirmed.
          return;
        }
      }
      if (broadcastToken) {
        const token = broadcastToken;
        broadcastToken = undefined;
        try {
          await api.post(url.Communication_url.release_broadcast_start, { live_seq: liveSeq, broadcast_token: token }, { timeout: 5000 });
          try {
            const key = `fanbird.broadcast.start.${liveSeq}`;
            if (sessionStorage.getItem(key) === token) sessionStorage.removeItem(key);
          } catch { /* The reservation was released even if browser storage is unavailable. */ }
        } catch { /* Abandoned start reservations expire automatically. */ }
      }
    };
    stopBroadcast.current = async () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(snapshotTimer);
      await release(true);
    };

    const start = async () => {
      try {
        if (!liveSeq || !selectedVideo_props || !selectedAudio_props || !canvas) throw new Error("방송과 촬영 장치를 확인해주세요.");
        if (!window.IVSBroadcastClient) throw new Error("송출 프로그램을 불러오지 못했습니다. 화면을 새로고침해주세요.");
        const streamConfig = window.IVSBroadcastClient.BASIC_FULL_HD_PORTRAIT;
        media = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: {exact: selectedVideo_props},
            width: {ideal: streamConfig.maxResolution.width},
            height: {ideal: streamConfig.maxResolution.height},
          },
          audio: {deviceId: {exact: selectedAudio_props}},
        });
        // Permission prompts can complete after navigation. Never keep their late tracks alive.
        if (cancelled) { media.getTracks().forEach(track => track.stop()); return; }
        mediaRef.current = media;
        cameraTrack = media.getTracks().find(track => track.kind === 'video');
        if (!cameraTrack || cameraTrack.readyState === 'ended') throw new Error("카메라를 연결한 뒤 방송을 시작해주세요.");
        cameraTrack.addEventListener?.('ended', onCameraEnded);
        media.getTracks().forEach(track => {
          track.enabled = track.kind === 'video' ? deviceEnabled.current.cameraEnabled : deviceEnabled.current.microphoneEnabled;
        });
        client = window.IVSBroadcastClient.create({streamConfig});
        await client.addVideoInputDevice(media, "Webcam", {index: 0});
        if (cancelled) return;
        cameraVideo = client.getVideoInputDevice("Webcam").element;
        fillCamera = () => {
          if (cancelled || !cameraVideo?.videoWidth || !cameraVideo.videoHeight) return;
          const frame = client.getCanvasDimensions();
          // IVS fits inside the supplied rectangle. Oversize it to crop centrally,
          // so both the preview and the published stream fill the portrait frame.
          const scale = Math.max(frame.width / cameraVideo.videoWidth, frame.height / cameraVideo.videoHeight);
          const width = Math.ceil(cameraVideo.videoWidth * scale);
          const height = Math.ceil(cameraVideo.videoHeight * scale);
          client.updateVideoDeviceComposition("Webcam", {
            index: 0, width, height,
            x: Math.floor((frame.width - width) / 2),
            y: Math.floor((frame.height - height) / 2),
          });
        };
        cameraVideo?.addEventListener('resize', fillCamera);
        fillCamera();
        await client.addAudioInputDevice(media, "Microphone");
        if (cancelled) return;
        const noticeCanvas = document.createElement('canvas');
        const dimensions = client.getCanvasDimensions();
        noticeCanvas.width = dimensions.width;
        noticeCanvas.height = dimensions.height;
        noticeCanvasRef.current = noticeCanvas;
        drawBroadcastDeviceNotice(noticeCanvas, deviceEnabled.current.cameraEnabled);
        await client.addImageSource(noticeCanvas, 'BroadcastDeviceNotice', {index: 1, x: 0, y: 0, width: noticeCanvas.width, height: noticeCanvas.height});
        if (cancelled) return;
        client.attachPreview(canvas);
        const res = await api.post(url.Communication_url.getStreamInfo, {live_seq: liveSeq}, {signal: controller.signal});
        broadcastToken = res.data?.broadcastToken;
        if (broadcastToken) {
          try { sessionStorage.setItem(`fanbird.broadcast.start.${liveSeq}`, broadcastToken); }
          catch { /* Abandoned reservations still expire when storage is unavailable. */ }
        }
        if (cancelled) { released = false; await release(); return; }
        if (!res.data?.streamKey || !res.data?.I_Epoint) throw new Error("방송서버 연결 정보를 확인하지 못했습니다.");
        if (cameraHasEnded()) throw new Error("카메라 연결이 끊겼습니다. 장치를 다시 연결해주세요.");
        startPending = client.startBroadcast(res.data.streamKey, res.data.I_Epoint);
        await startPending;
        startPending = undefined;
        if (cancelled) { try { await client.stopBroadcast(); } catch {} return; }
        if (cameraHasEnded()) throw new Error("카메라 연결이 끊겼습니다. 장치를 다시 연결해주세요.");
        // Commit server status only after IVS accepts the actual broadcast.
        await started();
        if (cancelled) return;
        setQrPublishing(!cameraLost);
        // Repeat the current state for viewers joining midway; never overlap requests.
        let audioStatePending = false;
        const publishAudioState = async () => {
          if (cancelled || audioStatePending) return;
          window.clearTimeout(audioStateTimer);
          audioStatePending = true;
          const muted = !deviceEnabled.current.microphoneEnabled;
          const cameraMuted = !deviceEnabled.current.cameraEnabled;
          try {
            await api.post(url.Communication_url.broadcast_audio_state, { live_seq: liveSeq, muted, camera_muted: cameraMuted },
              { signal: controller.signal, timeout: 5000 });
          } catch { /* A temporarily unavailable cue must not stop the broadcast. Retry below. */ }
          finally {
            audioStatePending = false;
            if (!cancelled) audioStateTimer = window.setTimeout(publishAudioState,
              muted !== !deviceEnabled.current.microphoneEnabled || cameraMuted !== !deviceEnabled.current.cameraEnabled ? 0 : 5000);
          }
        };
        publishAudioStateRef.current = () => { void publishAudioState(); };
        publishAudioStateRef.current();
        if (isRehearsal) return;
        snapshotTimer = window.setTimeout(async () => {
          try {
            const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
              result => result?.size ? resolve(result) : reject(new Error("라이브 캡처 이미지를 만들 수 없습니다.")), "image/jpeg", 0.86,
            ));
            if (cancelled) return;
            const formData = new FormData();
            formData.append("file", blob, "live-" + liveSeq + ".jpg");
            await api.post(url.Communication_url.upload_live_snapshot, formData, {
              params: {live_seq: liveSeq}, signal: controller.signal, headers: {"Content-Type": "multipart/form-data"},
            });
          } catch (error) {
            if (!cancelled) setSnapshotError("방송은 진행 중이지만 실시간 대표 이미지를 저장하지 못했습니다. 대표 이미지를 확인해주세요.");
          }
        }, 1200);
      } catch (error) {
        await release();
        if (!cancelled) failed(error);
      }
    };
    void start();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(snapshotTimer);
      void release();
    };
  }, [livestart, selectedVideo_props, selectedAudio_props, liveSeq, isRehearsal]);

  const hostStatus = (livestart === "start" || reviewHost)
    ? <LiveHostStatus interrupted={false} cameraOff={reviewHost?.cameraOff ?? !activeCamera} micMuted={reviewHost?.micMuted ?? !microphoneEnabled} />
    : null;
  const productCamera = liveSeq && <PcProductCameraAssistant liveSeq={Number(liveSeq)} active={qrPublishing && livestart === "start" && activeCamera} canvas={previewRef} />;
  return <Container translate="no"><LiveContent>
    <canvas ref={previewRef} hidden={livestart !== "start" && !reviewHost} width={1080} height={1920} aria-label="라이브 송출 미리보기" />
    {hostStatusTarget ? createPortal(hostStatus, hostStatusTarget) : hostStatus}
  </LiveContent>{productCameraTarget ? createPortal(productCamera, productCameraTarget) : productCameraTarget === undefined ? productCamera : null}{snapshotError && <p role="alert" style={{position: 'absolute', bottom: 12, left: 12, right: 12, margin: 0, padding: 12, background: 'var(--user-error-soft)', color: 'var(--user-error)', fontSize: 14, lineHeight: 1.6}}>{snapshotError}</p>}</Container>;
});

export default AdminLive;
